import { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface NfeDocument {
  id: number;
  ch_nfe: string;
  dh_emi: string | null;
  n_nf: string | null;
  serie: string | null;
  mod: string | null;
  nat_op: string | null;
  emit_cnpj: string | null;
  emit_xnome: string | null;
  dest_cnpj: string | null;
  dest_xnome: string | null;
  v_nf: number | null;
  v_prod: number | null;
  v_icms: number | null;
  v_ipi: number | null;
  v_pis: number | null;
  v_cofins: number | null;
  c_stat: string | null;
  x_motivo: string | null;
  n_prot: string | null;
  dh_recbto: string | null;
  raw_xml: string;
  payload: any;
  created_at: string;
}

interface NfeItem {
  id: number;
  ch_nfe: string;
  n_item: number;
  c_prod: string | null;
  x_prod: string | null;
  ncm: string | null;
  cfop: string | null;
  u_com: string | null;
  q_com: number | null;
  v_un_com: number | null;
  v_prod: number | null;
  impostos: any;
}

interface AssetPreview {
  seq: number;
  tag_patrimonial: string;
  numero_serie: string;
  descricao_normalizada: string;
  categoria_item: string;
  inventariavel: boolean;
  valor_base: number;
}

interface ItemDismemberment {
  enabled: boolean;
  qtd_ativos: number;
  divisao_tipo: 'nota' | 'custom';
  valor_unitario_custom: number;
  assets_preview: AssetPreview[];
}

export default function Invoices() {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<NfeDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [showXmlImportModal, setShowXmlImportModal] = useState(false);
  const [selectedXmlFile, setSelectedXmlFile] = useState<File | null>(null);
  const [isUploadingXml, setIsUploadingXml] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Estados do Drawer de Edição
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [editingDocument, setEditingDocument] = useState<NfeDocument | null>(null);
  const [editingItems, setEditingItems] = useState<NfeItem[]>([]);
  const [activeTab, setActiveTab] = useState<'details' | 'items' | 'notes' | 'xml'>('details');
  const [isSaving, setIsSaving] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);

  // Estados do Modal de Exclusão
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const [deleteType, setDeleteType] = useState<'all' | 'xml'>('all');
  const [isDeleting, setIsDeleting] = useState(false);

  // Campos editáveis
  const [editNatOp, setEditNatOp] = useState('');
  const [editObservacao, setEditObservacao] = useState('');
  const [editTags, setEditTags] = useState('');
  const [editCategoria, setEditCategoria] = useState('');
  const [editCentroCusto, setEditCentroCusto] = useState('');
  const [editStatus, setEditStatus] = useState<'imported' | 'reviewed' | 'linked_asset' | 'ignored'>('imported');

  // Estados de desmembramento patrimonial
  const [itemDismemberments, setItemDismemberments] = useState<Map<number, ItemDismemberment>>(new Map());
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [isGeneratingAssets, setIsGeneratingAssets] = useState(false);

  const [isExportingSAP, setIsExportingSAP] = useState(false);

  useEffect(() => {
    if (user) {
      fetchDocuments();
    }
  }, [user]);

  const fetchDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('nfe_documents')
        .select('ch_nfe, n_nf, serie, emit_xnome, dest_xnome, dh_emi, v_nf, c_stat, created_at')
        .order('dh_emi', { ascending: false })
        .limit(50);

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Erro ao buscar documentos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditDrawer = async (ch_nfe: string, readOnly: boolean = false) => {
    setShowEditDrawer(true);
    setLoadingDetails(true);
    setActiveTab('details');
    setIsReadOnly(readOnly);

    try {
      // Buscar documento completo
      const { data: doc, error: docError } = await supabase
        .from('nfe_documents')
        .select('*')
        .eq('ch_nfe', ch_nfe)
        .single();

      if (docError) throw docError;

      // Buscar itens
      const { data: items, error: itemsError } = await supabase
        .from('nfe_items')
        .select('*')
        .eq('ch_nfe', ch_nfe)
        .order('n_item', { ascending: true });

      if (itemsError) throw itemsError;

      setEditingDocument(doc);
      setEditingItems(items || []);

      // Preencher campos editáveis
      setEditNatOp(doc.nat_op || '');
      setEditObservacao(doc.payload?.observacao_interna || '');
      setEditTags(doc.payload?.tags?.join(', ') || '');
      setEditCategoria(doc.payload?.categoria_ativo || '');
      setEditCentroCusto(doc.payload?.centro_custo || '');
      setEditStatus(doc.payload?.status_processamento || 'imported');

    } catch (error) {
      console.error('Erro ao carregar detalhes:', error);
      alert('Erro ao carregar detalhes da NF-e');
      setShowEditDrawer(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!editingDocument) return;

    setIsSaving(true);

    try {
      // Atualizar documento
      const updatedPayload = {
        ...editingDocument.payload,
        observacao_interna: editObservacao,
        tags: editTags.split(',').map(t => t.trim()).filter(t => t),
        categoria_ativo: editCategoria,
        centro_custo: editCentroCusto,
        status_processamento: editStatus
      };

      const { error: docError } = await supabase
        .from('nfe_documents')
        .update({
          nat_op: editNatOp,
          payload: updatedPayload
        })
        .eq('ch_nfe', editingDocument.ch_nfe);

      if (docError) throw docError;

      // Atualizar itens
      for (const item of editingItems) {
        const { error: itemError } = await supabase
          .from('nfe_items')
          .update({
            x_prod: item.x_prod,
            impostos: item.impostos
          })
          .eq('id', item.id);

        if (itemError) throw itemError;
      }

      // 🆕 EXPORTAR DADOS PARA CADASTRO DE ATIVOS
      console.log('🚀 Iniciando exportação para cadastro de ativos...');

      // Verificar sessão do usuário
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.error('❌ Erro de sessão:', sessionError);
        throw new Error('Sessão expirada. Faça login novamente.');
      }

      // Para cada item que está marcado como "ativo_sugerido" ou tem dados de classificação
      const itensParaExportar = editingItems.filter(item => {
        const classificacao = item.impostos?.classificacao;
        return classificacao && (
          classificacao.ativo_sugerido || 
          classificacao.patrimonio_tag || 
          classificacao.numero_serie ||
          classificacao.categoria_item
        );
      });

      console.log('📦 Itens para exportar:', itensParaExportar.length);

      if (itensParaExportar.length > 0) {
        // Buscar campos dinâmicos existentes
        const { data: existingFields, error: fieldsError } = await supabase
          .from('dynamic_fields')
          .select('field_name');

        if (fieldsError) {
          console.error('❌ Erro ao buscar campos:', fieldsError);
          throw fieldsError;
        }

        const existingFieldNames = new Set(existingFields?.map(f => f.field_name) || []);

        // Campos necessários para o cadastro de ativos
        const requiredFields = [
          'Número da Nota',
          'Número do Ativo',
          'Descrição',
          'Número de Série',
          'Valor',
          'Categoria',
          'Centro de Custo',
          'Tag Patrimonial'
        ];

        // Criar campos que não existem
        const fieldsToCreate = requiredFields
          .filter(fieldName => !existingFieldNames.has(fieldName))
          .map((fieldName, index) => ({
            field_name: fieldName,
            field_type: 'text',
            field_order: (existingFields?.length || 0) + index,
            is_required: false,
            created_by: session.user.id
          }));

        if (fieldsToCreate.length > 0) {
          console.log('📋 Criando campos dinâmicos:', fieldsToCreate.length);
          
          for (const field of fieldsToCreate) {
            const { error: insertError } = await supabase
              .from('dynamic_fields')
              .insert([field]);

            if (insertError && !insertError.message.includes('duplicate')) {
              console.error(`❌ Erro ao criar campo "${field.field_name}":`, insertError);
            } else {
              console.log(`✅ Campo "${field.field_name}" criado`);
            }
          }
        }

        // Criar ativos na tabela assets
        const assetsToCreate = [];

        for (const item of itensParaExportar) {
          const classificacao = item.impostos?.classificacao || {};
          
          // Montar dados dinâmicos do ativo
          const dynamicData: Record<string, string> = {
            'Número da Nota': `${editingDocument.n_nf}-${editingDocument.serie}`,
            'Número do Ativo': String(item.n_item),
            'Descrição': item.x_prod || '',
            'Número de Série': classificacao.numero_serie || '',
            'Valor': String(item.v_prod || 0),
            'Categoria': classificacao.categoria_item || editCategoria || '',
            'Centro de Custo': editCentroCusto || '',
            'Tag Patrimonial': classificacao.patrimonio_tag || ''
          };

          // Verificar se já existe um ativo com essa combinação ch_nfe + n_item
          const { data: existingAsset } = await supabase
            .from('assets')
            .select('id')
            .eq('asset_code', `${editingDocument.ch_nfe}-${item.n_item}`)
            .single();

          if (!existingAsset) {
            assetsToCreate.push({
              asset_code: `${editingDocument.ch_nfe}-${item.n_item}`,
              dynamic_data: dynamicData,
              status: 'active',
              created_by: session.user.id
            });
          } else {
            console.log(`ℹ️ Ativo já existe para o item ${item.n_item}`);
            
            // Atualizar ativo existente
            await supabase
              .from('assets')
              .update({
                dynamic_data: dynamicData,
                updated_at: new Date().toISOString()
              })
              .eq('id', existingAsset.id);
          }
        }

        // Inserir novos ativos
        if (assetsToCreate.length > 0) {
          console.log('💾 Criando ativos:', assetsToCreate.length);
          
          const { data: insertedAssets, error: assetsError } = await supabase
            .from('assets')
            .insert(assetsToCreate)
            .select();

          if (assetsError) {
            console.error('❌ Erro ao criar ativos:', assetsError);
            throw assetsError;
          }

          console.log('✅ Ativos criados com sucesso:', insertedAssets?.length || 0);
        }

        alert(`✅ Alterações salvas com sucesso!\n\n📦 ${itensParaExportar.length} ativo(s) exportado(s) para o cadastro.`);
      } else {
        alert('✅ Alterações salvas com sucesso!');
      }

      setShowEditDrawer(false);
      fetchDocuments();

    } catch (error) {
      console.error('❌ Erro ao salvar:', error);
      alert(`❌ Erro ao salvar alterações: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleItemFieldChange = (itemId: number, field: string, value: any) => {
    setEditingItems(prev => prev.map(item => {
      if (item.id === itemId) {
        if (field === 'x_prod') {
          return { ...item, x_prod: value };
        } else {
          // Campos de classificação vão para impostos.classificacao
          return {
            ...item,
            impostos: {
              ...item.impostos,
              classificacao: {
                ...(item.impostos?.classificacao || {}),
                [field]: value
              }
            }
          };
        }
      }
      return item;
    }));
  };

  // Funções de desmembramento patrimonial
  const toggleDismemberment = (itemId: number, item: NfeItem) => {
    const newMap = new Map(itemDismemberments);
    const current = newMap.get(itemId);
    
    if (current?.enabled) {
      newMap.delete(itemId);
    } else {
      newMap.set(itemId, {
        enabled: true,
        qtd_ativos: item.q_com || 1,
        divisao_tipo: 'nota',
        valor_unitario_custom: item.v_un_com || 0,
        assets_preview: []
      });
    }
    
    setItemDismemberments(newMap);
  };

  const updateDismemberment = (itemId: number, field: keyof ItemDismemberment, value: any) => {
    const newMap = new Map(itemDismemberments);
    const current = newMap.get(itemId);
    
    if (current) {
      newMap.set(itemId, {
        ...current,
        [field]: value
      });
      setItemDismemberments(newMap);
    }
  };

  const generateAssetsPreview = (itemId: number, item: NfeItem) => {
    const dismemberment = itemDismemberments.get(itemId);
    if (!dismemberment) return;

    const qtd = dismemberment.qtd_ativos;
    const valorBase = dismemberment.divisao_tipo === 'nota' 
      ? (item.v_prod || 0) / qtd 
      : dismemberment.valor_unitario_custom;

    const preview: AssetPreview[] = [];
    for (let i = 1; i <= qtd; i++) {
      preview.push({
        seq: i,
        tag_patrimonial: '',
        numero_serie: '',
        descricao_normalizada: item.x_prod || '',
        categoria_item: item.impostos?.classificacao?.categoria_item || '',
        inventariavel: true,
        valor_base: valorBase
      });
    }

    updateDismemberment(itemId, 'assets_preview', preview);
    
    // Expandir o item para mostrar a pré-visualização
    const newExpanded = new Set(expandedItems);
    newExpanded.add(itemId);
    setExpandedItems(newExpanded);
  };

  const updateAssetPreview = (itemId: number, seq: number, field: keyof AssetPreview, value: any) => {
    const newMap = new Map(itemDismemberments);
    const current = newMap.get(itemId);
    
    if (current) {
      const updatedAssets = current.assets_preview.map(asset => 
        asset.seq === seq ? { ...asset, [field]: value } : asset
      );
      
      newMap.set(itemId, {
        ...current,
        assets_preview: updatedAssets
      });
      setItemDismemberments(newMap);
    }
  };

  const toggleItemExpansion = (itemId: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const validateDismemberments = (): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    itemDismemberments.forEach((dismemberment, itemId) => {
      if (!dismemberment.enabled) return;
      
      const item = editingItems.find(i => i.id === itemId);
      if (!item) return;

      // Validar se tem pré-visualização gerada
      if (dismemberment.assets_preview.length === 0) {
        errors.push(`Item ${item.n_item}: Clique em "Pré-visualizar ativos" antes de gerar`);
        return;
      }

      // Validar tags patrimoniais
      const emptyTags = dismemberment.assets_preview.filter(a => !a.tag_patrimonial.trim());
      if (emptyTags.length > 0) {
        errors.push(`Item ${item.n_item}: ${emptyTags.length} ativo(s) sem tag patrimonial`);
      }

      // Validar tags duplicadas
      const tags = dismemberment.assets_preview.map(a => a.tag_patrimonial.trim()).filter(t => t);
      const uniqueTags = new Set(tags);
      if (tags.length !== uniqueTags.size) {
        errors.push(`Item ${item.n_item}: Tags patrimoniais duplicadas`);
      }

      // Validar valor total
      const totalGerado = dismemberment.assets_preview.reduce((sum, a) => sum + a.valor_base, 0);
      const valorItem = item.v_prod || 0;
      
      if (dismemberment.divisao_tipo === 'custom') {
        const totalNF = editingDocument?.v_nf || 0;
        if (totalGerado > totalNF) {
          errors.push(`Item ${item.n_item}: Valor total gerado (R$ ${totalGerado.toFixed(2)}) excede o total da NF-e`);
        }
      }
    });

    return { valid: errors.length === 0, errors };
  };

  const calculateAccessoryCosts = () => {
    if (!editingDocument) return null;

    const vProdTotal = editingDocument.v_prod || 0;
    const vFrete = 0; // Extrair do payload se disponível
    const vImpostos = (editingDocument.v_icms || 0) + (editingDocument.v_ipi || 0) + 
                      (editingDocument.v_pis || 0) + (editingDocument.v_cofins || 0);
    const vSeguro = 0; // Extrair do payload se disponível
    const vOutros = (editingDocument.v_nf || 0) - vProdTotal - vImpostos;

    return {
      frete: vFrete,
      impostos: vImpostos,
      seguro: vSeguro,
      outros: Math.max(0, vOutros)
    };
  };

  const handleGenerateAssets = async () => {
    if (!editingDocument) return;

    // Validar antes de gerar
    const validation = validateDismemberments();
    if (!validation.valid) {
      alert('❌ Erros encontrados:\n\n' + validation.errors.join('\n'));
      return;
    }

    if (!confirm('Confirma a geração dos ativos patrimoniais?\n\nEsta ação criará os itens principais e sub-itens financeiros no sistema.')) {
      return;
    }

    setIsGeneratingAssets(true);

    try {
      const accessoryCosts = calculateAccessoryCosts();
      if (!accessoryCosts) throw new Error('Erro ao calcular custos acessórios');

      const vProdTotal = editingDocument.v_prod || 0;

      // Para cada item com desmembramento
      for (const [itemId, dismemberment] of itemDismemberments.entries()) {
        if (!dismemberment.enabled || dismemberment.assets_preview.length === 0) continue;

        const item = editingItems.find(i => i.id === itemId);
        if (!item) continue;

        const valorItem = item.v_prod || 0;
        const percentualItem = vProdTotal > 0 ? valorItem / vProdTotal : 0;

        // Calcular custos acessórios proporcionais ao item
        const freteItem = accessoryCosts.frete * percentualItem;
        const impostosItem = accessoryCosts.impostos * percentualItem;
        const seguroItem = accessoryCosts.seguro * percentualItem;
        const outrosItem = accessoryCosts.outros * percentualItem;

        const qtdAtivos = dismemberment.assets_preview.length;

        // Para cada ativo do item
        for (let i = 0; i < dismemberment.assets_preview.length; i++) {
          const asset = dismemberment.assets_preview[i];
          const isLast = i === dismemberment.assets_preview.length - 1;

          // Calcular valores base e acessórios por unidade
          const valorBaseUnit = asset.valor_base;
          const percentualUnit = valorItem > 0 ? valorBaseUnit / valorItem : 1 / qtdAtivos;

          let freteUnit = (freteItem * percentualUnit);
          let impostosUnit = (impostosItem * percentualUnit);
          let seguroUnit = (seguroItem * percentualUnit);
          let outrosUnit = (outrosItem * percentualUnit);

          // Arredondar para 2 casas
          freteUnit = Math.round(freteUnit * 100) / 100;
          impostosUnit = Math.round(impostosUnit * 100) / 100;
          seguroUnit = Math.round(seguroUnit * 100) / 100;
          outrosUnit = Math.round(outrosUnit * 100) / 100;

          // Ajuste de resíduo no último ativo
          if (isLast) {
            const somaAtual = dismemberment.assets_preview.slice(0, -1).reduce((sum, a) => {
              const vBase = a.valor_base;
              const pct = valorItem > 0 ? vBase / valorItem : 1 / qtdAtivos;
              return sum + vBase + 
                     Math.round((freteItem * pct) * 100) / 100 +
                     Math.round((impostosItem * pct) * 100) / 100 +
                     Math.round((seguroItem * pct) * 100) / 100 +
                     Math.round((outrosItem * pct) * 100) / 100;
            }, 0);

            const totalEsperado = valorItem + freteItem + impostosItem + seguroItem + outrosItem;
            const residuo = totalEsperado - somaAtual - valorBaseUnit - freteUnit - impostosUnit - seguroUnit - outrosUnit;
            
            outrosUnit += residuo;
            outrosUnit = Math.round(outrosUnit * 100) / 100;
          }

          const valorTotalUnit = valorBaseUnit + freteUnit + impostosUnit + seguroUnit + outrosUnit;

          // Inserir item principal
          const { data: mainItem, error: mainError } = await supabase
            .from('asset_main_items')
            .insert({
              ch_nfe: editingDocument.ch_nfe,
              n_item: item.n_item,
              seq: asset.seq,
              tag_patrimonial: asset.tag_patrimonial.trim(),
              status_inventario: 'NAO_INVENTARIADO',
              descricao_normalizada: asset.descricao_normalizada,
              categoria_item: asset.categoria_item,
              numero_serie: asset.numero_serie || null,
              valor_total: valorTotalUnit
            })
            .select()
            .single();

          if (mainError) throw mainError;

          // Inserir sub-itens financeiros
          const subitems = [];

          // BASE
          subitems.push({
            main_item_id: mainItem.id,
            sub_code: `${asset.tag_patrimonial}.1`,
            sub_type: 'BASE',
            valor: valorBaseUnit,
            status_inventario: 'NAO_INVENTARIADO'
          });

          // FRETE
          if (freteUnit > 0) {
            subitems.push({
              main_item_id: mainItem.id,
              sub_code: `${asset.tag_patrimonial}.2`,
              sub_type: 'FRETE',
              valor: freteUnit,
              status_inventario: 'NAO_INVENTARIADO'
            });
          }

          // IMPOSTOS
          if (impostosUnit > 0) {
            subitems.push({
              main_item_id: mainItem.id,
              sub_code: `${asset.tag_patrimonial}.3`,
              sub_type: 'IMPOSTOS',
              valor: impostosUnit,
              status_inventario: 'NAO_INVENTARIADO'
            });
          }

          // SEGURO
          if (seguroUnit > 0) {
            subitems.push({
              main_item_id: mainItem.id,
              sub_code: `${asset.tag_patrimonial}.4`,
              sub_type: 'SEGURO',
              valor: seguroUnit,
              status_inventario: 'NAO_INVENTARIADO'
            });
          }

          // OUTROS
          if (outrosUnit > 0) {
            subitems.push({
              main_item_id: mainItem.id,
              sub_code: `${asset.tag_patrimonial}.5`,
              sub_type: 'OUTROS',
              valor: outrosUnit,
              status_inventario: 'NAO_INVENTARIADO'
            });
          }

          const { error: subitemsError } = await supabase
            .from('asset_financial_subitems')
            .insert(subitems);

          if (subitemsError) throw subitemsError;

          // Log de auditoria
          await supabase
            .from('asset_audit_log')
            .insert({
              ch_nfe: editingDocument.ch_nfe,
              n_item: item.n_item,
              main_item_id: mainItem.id,
              action: 'DESMEMBRAMENTO_CRIADO',
              payload: {
                dismemberment_config: dismemberment,
                asset_data: asset,
                calculated_values: {
                  valor_base: valorBaseUnit,
                  frete: freteUnit,
                  impostos: impostosUnit,
                  seguro: seguroUnit,
                  outros: outrosUnit,
                  total: valorTotalUnit
                },
                timestamp: new Date().toISOString()
              }
            });
        }
      }

      alert('✅ Ativos patrimoniais gerados com sucesso!\n\nOs itens principais e sub-itens financeiros foram criados no sistema.');
      
      // Limpar desmembramentos
      setItemDismemberments(new Map());
      setExpandedItems(new Set());

    } catch (error: any) {
      console.error('Erro ao gerar ativos:', error);
      alert(`❌ Erro ao gerar ativos:\n\n${error?.message || 'Erro desconhecido'}`);
    } finally {
      setIsGeneratingAssets(false);
    }
  };

  const normalizeXmlToOneLine = (s: string) =>
    s.replace(/\r?\n|\r/g, '').replace(/\s{2,}/g, ' ').trim();

  const looksLikeNfe = (xml: string) =>
    /<NFe[\s>]|<nfeProc[\s>]/i.test(xml);

  const getText = (doc: Document, tag: string) =>
    doc.getElementsByTagName(tag)[0]?.textContent?.trim() || '';

  const getMultipleTexts = (doc: Document, tag: string): string[] => {
    const elements = doc.getElementsByTagName(tag);
    const results: string[] = [];
    for (let i = 0; i < elements.length; i++) {
      const text = elements[i]?.textContent?.trim();
      if (text) results.push(text);
    }
    return results;
  };

  const extractAccessKey = (doc: Document) => {
    const inf = doc.getElementsByTagName('infNFe')[0];
    const idAttr = inf?.getAttribute('Id') || '';
    const m = idAttr.match(/NFe(\d{44})/);
    return m?.[1] || '';
  };

  const extractItems = (doc: Document): any[] => {
    const items: any[] = [];
    const detElements = doc.getElementsByTagName('det');
    
    for (let i = 0; i < detElements.length; i++) {
      const det = detElements[i];
      const getItemText = (tag: string) => {
        const elem = det.getElementsByTagName(tag)[0];
        return elem?.textContent?.trim() || '';
      };

      const nItem = det.getAttribute('nItem') || (i + 1).toString();

      const impostos: any = {};
      
      const icms = det.getElementsByTagName('ICMS')[0];
      if (icms) {
        impostos.icms = {
          orig: getItemText('orig'),
          cst: getItemText('CST'),
          modBC: getItemText('modBC'),
          vBC: getItemText('vBC'),
          pICMS: getItemText('pICMS'),
          vICMS: getItemText('vICMS')
        };
      }

      const ipi = det.getElementsByTagName('IPI')[0];
      if (ipi) {
        impostos.ipi = {
          cst: getItemText('CST'),
          vBC: getItemText('vBC'),
          pIPI: getItemText('pIPI'),
          vIPI: getItemText('vIPI')
        };
      }

      const pis = det.getElementsByTagName('PIS')[0];
      if (pis) {
        impostos.pis = {
          cst: getItemText('CST'),
          vBC: getItemText('vBC'),
          pPIS: getItemText('pPIS'),
          vPIS: getItemText('vPIS')
        };
      }

      const cofins = det.getElementsByTagName('COFINS')[0];
      if (cofins) {
        impostos.cofins = {
          cst: getItemText('CST'),
          vBC: getItemText('vBC'),
          pCOFINS: getItemText('pCOFINS'),
          vCOFINS: getItemText('vCOFINS')
        };
      }

      items.push({
        n_item: parseInt(nItem),
        c_prod: getItemText('cProd'),
        x_prod: getItemText('xProd'),
        ncm: getItemText('NCM'),
        cfop: getItemText('CFOP'),
        u_com: getItemText('uCom'),
        q_com: parseFloat(getItemText('qCom')) || null,
        v_un_com: parseFloat(getItemText('vUnCom')) || null,
        v_prod: parseFloat(getItemText('vProd')) || null,
        impostos
      });
    }

    return items;
  };

  const xmlToJson = (xmlText: string): any => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

    const parserError = xmlDoc.getElementsByTagName('parsererror');
    if (parserError.length > 0) {
      throw new Error('XML inválido ou malformado');
    }

    const ch_nfe = extractAccessKey(xmlDoc);
    const n_nf = getText(xmlDoc, 'nNF');
    const serie = getText(xmlDoc, 'serie');
    
    const cnpjList = getMultipleTexts(xmlDoc, 'CNPJ');
    const xNomeList = getMultipleTexts(xmlDoc, 'xNome');

    const jsonData: any = {
      ch_nfe,
      dh_emi: getText(xmlDoc, 'dhEmi') || getText(xmlDoc, 'dEmi') || null,
      n_nf: n_nf || null,
      serie: serie || null,
      mod: getText(xmlDoc, 'mod') || null,
      nat_op: getText(xmlDoc, 'natOp') || null,

      emit_cnpj: cnpjList[0] || null,
      emit_xnome: xNomeList[0] || null,
      dest_cnpj: cnpjList[1] || null,
      dest_xnome: xNomeList[1] || null,

      v_nf: parseFloat(getText(xmlDoc, 'vNF')) || null,
      v_prod: parseFloat(getText(xmlDoc, 'vProd')) || null,
      v_icms: parseFloat(getText(xmlDoc, 'vICMS')) || null,
      v_ipi: parseFloat(getText(xmlDoc, 'vIPI')) || null,
      v_pis: parseFloat(getText(xmlDoc, 'vPIS')) || null,
      v_cofins: parseFloat(getText(xmlDoc, 'vCOFINS')) || null,

      c_stat: getText(xmlDoc, 'cStat') || null,
      x_motivo: getText(xmlDoc, 'xMotivo') || null,
      n_prot: getText(xmlDoc, 'nProt') || null,
      dh_recbto: getText(xmlDoc, 'dhRecbto') || null,

      payload: {
        emitente: {
          razao_social: xNomeList[0] || '',
          nome_fantasia: getText(xmlDoc, 'xFant'),
          cnpj: cnpjList[0] || '',
          inscricao_estadual: getText(xmlDoc, 'IE'),
          endereco: {
            logradouro: getText(xmlDoc, 'xLgr'),
            numero: getText(xmlDoc, 'nro'),
            complemento: getText(xmlDoc, 'xCpl'),
            bairro: getText(xmlDoc, 'xBairro'),
            municipio: getText(xmlDoc, 'xMun'),
            uf: getText(xmlDoc, 'UF'),
            cep: getText(xmlDoc, 'CEP'),
          },
          contato: {
            telefone: getText(xmlDoc, 'fone'),
            email: getText(xmlDoc, 'email'),
          },
        },
        destinatario: {
          razao_social: xNomeList[1] || '',
          cnpj: cnpjList[1] || '',
          cpf: getText(xmlDoc, 'CPF'),
          inscricao_estadual: getMultipleTexts(xmlDoc, 'IE')[1] || '',
        },
        transporte: {
          modalidade_frete: getText(xmlDoc, 'modFrete'),
          transportadora: {
            razao_social: xNomeList[2] || '',
            cnpj: cnpjList[2] || '',
          },
        },
        pagamento: {
          forma_pagamento: getText(xmlDoc, 'tPag'),
          valor_pagamento: getText(xmlDoc, 'vPag'),
        },
        informacoes_adicionais: {
          informacoes_complementares: getText(xmlDoc, 'infCpl'),
          informacoes_fisco: getText(xmlDoc, 'infAdFisco'),
        },
        metadata: {
          imported_at: new Date().toISOString(),
        },
        status_processamento: 'imported'
      },

      items: extractItems(xmlDoc)
    };

    return jsonData;
  };

  const handleXmlFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedXmlFile(file);
    }
  };

  const handleQuickXmlUpload = async () => {
    if (!selectedXmlFile || !user) return;

    setIsUploadingXml(true);
    setUploadProgress(0);

    try {
      setUploadProgress(10);

      const lower = (selectedXmlFile.name || '').toLowerCase();
      if (!lower.endsWith('.xml')) {
        throw new Error('Por favor, envie um arquivo com extensão .xml');
      }

      setUploadProgress(20);

      const raw = await selectedXmlFile.text();
      if (!raw || raw.trim().length < 10) {
        throw new Error('Arquivo XML vazio ou inválido');
      }

      setUploadProgress(30);

      if (!raw.trim().startsWith('<')) {
        throw new Error('Conteúdo não parece ser um XML válido');
      }

      setUploadProgress(40);

      const xmlOneLine = normalizeXmlToOneLine(raw);

      if (!looksLikeNfe(xmlOneLine)) {
        throw new Error('XML não parece ser uma NF-e válida (tags <NFe> ou <nfeProc> não encontradas)');
      }

      setUploadProgress(50);

      const jsonPayload = xmlToJson(raw);

      setUploadProgress(60);

      const ch_nfe = jsonPayload.ch_nfe;
      if (!ch_nfe) {
        throw new Error('Chave de acesso não encontrada no XML');
      }

      setUploadProgress(70);

      const { data: existingDoc } = await supabase
        .from('nfe_documents')
        .select('id')
        .eq('ch_nfe', ch_nfe)
        .single();

      setUploadProgress(80);

      if (existingDoc) {
        const { error } = await supabase
          .from('nfe_documents')
          .update({
            dh_emi: jsonPayload.dh_emi,
            n_nf: jsonPayload.n_nf,
            serie: jsonPayload.serie,
            mod: jsonPayload.mod,
            nat_op: jsonPayload.nat_op,
            emit_cnpj: jsonPayload.emit_cnpj,
            emit_xnome: jsonPayload.emit_xnome,
            dest_cnpj: jsonPayload.dest_cnpj,
            dest_xnome: jsonPayload.dest_xnome,
            v_nf: jsonPayload.v_nf,
            v_prod: jsonPayload.v_prod,
            v_icms: jsonPayload.v_icms,
            v_ipi: jsonPayload.v_ipi,
            v_pis: jsonPayload.v_pis,
            v_cofins: jsonPayload.v_cofins,
            c_stat: jsonPayload.c_stat,
            x_motivo: jsonPayload.x_motivo,
            n_prot: jsonPayload.n_prot,
            dh_recbto: jsonPayload.dh_recbto,
            raw_xml: xmlOneLine,
            payload: jsonPayload.payload
          })
          .eq('ch_nfe', ch_nfe);

        if (error) throw error;

        await supabase
          .from('nfe_items')
          .delete()
          .eq('ch_nfe', ch_nfe);

      } else {
        const { error } = await supabase
          .from('nfe_documents')
          .insert({
            ch_nfe: jsonPayload.ch_nfe,
            dh_emi: jsonPayload.dh_emi,
            n_nf: jsonPayload.n_nf,
            serie: jsonPayload.serie,
            mod: jsonPayload.mod,
            nat_op: jsonPayload.nat_op,
            emit_cnpj: jsonPayload.emit_cnpj,
            emit_xnome: jsonPayload.emit_xnome,
            dest_cnpj: jsonPayload.dest_cnpj,
            dest_xnome: jsonPayload.dest_xnome,
            v_nf: jsonPayload.v_nf,
            v_prod: jsonPayload.v_prod,
            v_icms: jsonPayload.v_icms,
            v_ipi: jsonPayload.v_ipi,
            v_pis: jsonPayload.v_pis,
            v_cofins: jsonPayload.v_cofins,
            c_stat: jsonPayload.c_stat,
            x_motivo: jsonPayload.x_motivo,
            n_prot: jsonPayload.n_prot,
            dh_recbto: jsonPayload.dh_recbto,
            raw_xml: xmlOneLine,
            payload: jsonPayload.payload
          });

        if (error) throw error;
      }

      if (jsonPayload.items && jsonPayload.items.length > 0) {
        const itemsToInsert = jsonPayload.items.map((item: any) => ({
          ch_nfe: ch_nfe,
          n_item: item.n_item,
          c_prod: item.c_prod,
          x_prod: item.x_prod,
          ncm: item.ncm,
          cfop: item.cfop,
          u_com: item.u_com,
          q_com: item.q_com,
          v_un_com: item.v_un_com,
          v_prod: item.v_prod,
          impostos: item.impostos
        }));

        const { error: itemsError } = await supabase
          .from('nfe_items')
          .insert(itemsToInsert);

        if (itemsError) throw itemsError;
      }

      setUploadProgress(100);
      
      setShowXmlImportModal(false);
      setSelectedXmlFile(null);
      
      fetchDocuments();
      
      alert(`✅ NF-e importada com sucesso!\n\n📋 Chave: ${ch_nfe}\n📄 Número: ${jsonPayload.n_nf}-${jsonPayload.serie}\n💰 Valor: R$ ${jsonPayload.v_nf?.toFixed(2) || '0.00'}\n🏢 Emitente: ${jsonPayload.emit_xnome || 'N/A'}`);
      
    } catch (e: any) {
      console.error('Erro na importação:', e);
      alert(`❌ Erro ao importar NF-e XML:\n\n${e?.message || 'Erro desconhecido'}`);
    } finally {
      setIsUploadingXml(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteDocument = async (ch_nfe: string) => {
    if (!confirm('Tem certeza que deseja excluir esta NF-e?')) return;

    try {
      const { error } = await supabase
        .from('nfe_documents')
        .delete()
        .eq('ch_nfe', ch_nfe);

      if (error) throw error;
      fetchDocuments();
      alert('NF-e excluída com sucesso!');
    } catch (error) {
      console.error('Erro ao excluir NF-e:', error);
      alert('Erro ao excluir NF-e');
    }
  };

  const handleOpenDeleteModal = (ch_nfe: string) => {
    setDocumentToDelete(ch_nfe);
    setDeleteType('all');
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!documentToDelete) return;

    setIsDeleting(true);

    try {
      if (deleteType === 'all') {
        // Excluir lançamento completo (cascata automática para itens)
        const { error: docError } = await supabase
          .from('nfe_documents')
          .delete()
          .eq('ch_nfe', documentToDelete);

        if (docError) throw docError;

        // Tentar excluir arquivo XML do storage (se existir)
        try {
          const { error: storageError } = await supabase.storage
            .from('nfe_files')
            .remove([`${documentToDelete}.xml`]);
          
          if (storageError) console.warn('Arquivo XML não encontrado no storage:', storageError);
        } catch (storageErr) {
          console.warn('Erro ao tentar excluir XML do storage:', storageErr);
        }

        alert('✅ Lançamento excluído com sucesso!');
      } else {
        // Excluir apenas o arquivo XML do storage
        const { error: storageError } = await supabase.storage
          .from('nfe_files')
          .remove([`${documentToDelete}.xml`]);

        if (storageError) throw storageError;

        alert('✅ Arquivo XML excluído com sucesso! O lançamento foi mantido.');
      }

      setShowDeleteModal(false);
      setDocumentToDelete(null);
      fetchDocuments();

    } catch (error) {
      console.error('Erro ao excluir:', error);
      alert(`❌ Erro ao excluir: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatXml = (xml: string) => {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xml, 'text/xml');
      const serializer = new XMLSerializer();
      let formatted = serializer.serializeToString(xmlDoc);
      
      formatted = formatted.replace(/></g, '>\n<');
      
      return formatted;
    } catch (error) {
      return xml;
    }
  };

  const generateSAPBAPIFile = (items: NfeItem[], document: NfeDocument) => {
    const lines: string[] = [];
    
    // Header do arquivo BAPI
    lines.push('*-------------------------------------------------------------------*');
    lines.push('* BAPI para Importação de Ativos Fixos - SAP');
    lines.push('* Gerado automaticamente pelo AssetControl');
    lines.push(`* Data: ${new Date().toLocaleString('pt-BR')}`);
    lines.push('*-------------------------------------------------------------------*');
    lines.push('');
    
    // Estrutura BAPI_ACC_ASSET_CREATE
    lines.push('REPORT Z_IMPORT_ATIVOS_ASSETCONTROL.');
    lines.push('');
    lines.push('DATA: lt_asset_master TYPE TABLE OF bapi1022_feglg001,');
    lines.push('      ls_asset_master TYPE bapi1022_feglg001,');
    lines.push('      lt_asset_general TYPE TABLE OF bapi1022_feglg003,');
    lines.push('      ls_asset_general TYPE bapi1022_feglg003,');
    lines.push('      lt_asset_time TYPE TABLE OF bapi1022_feglg017,');
    lines.push('      ls_asset_time TYPE bapi1022_feglg017,');
    lines.push('      lt_return TYPE TABLE OF bapiret2,');
    lines.push('      ls_return TYPE bapiret2,');
    lines.push('      lv_asset_number TYPE anla-anln1.');
    lines.push('');
    lines.push('START-OF-SELECTION.');
    lines.push('');

    // Processar cada item
    items.forEach((item, index) => {
      const classificacao = item.impostos?.classificacao || {};
      const itemNumber = index + 1;
      
      lines.push(`* ============================================`);
      lines.push(`* Item ${itemNumber}: ${item.x_prod}`);
      lines.push(`* ============================================`);
      lines.push('');
      
      // Dados Mestres do Ativo
      lines.push('  CLEAR: ls_asset_master, ls_asset_general, ls_asset_time.');
      lines.push('');
      
      // BAPI1022_FEGLG001 - Dados Gerais
      lines.push('  ls_asset_master-comp_code = \'1000\'.  " Código da Empresa');
      lines.push(`  ls_asset_master-asset_class = '${classificacao.categoria_item || 'EQUIP'}'.  " Classe do Ativo`);
      lines.push(`  ls_asset_master-descript = '${(item.x_prod || '').substring(0, 50)}'.  " Descrição`);
      lines.push('');
      
      // BAPI1022_FEGLG003 - Dados Adicionais
      lines.push(`  ls_asset_general-inventory_no = '${classificacao.patrimonio_tag || ''}'.  " Número de Inventário`);
      lines.push(`  ls_asset_general-serial_no = '${classificacao.numero_serie || ''}'.  " Número de Série`);
      lines.push(`  ls_asset_general-quantity = ${item.q_com || 1}.  " Quantidade`);
      lines.push('');
      
      // BAPI1022_FEGLG017 - Dados Temporais
      lines.push(`  ls_asset_time-cap_date = '${document.dh_emi ? new Date(document.dh_emi).toISOString().split('T')[0].replace(/-/g, '') : ''}'. " Data de Capitalização`);
      lines.push(`  ls_asset_time-acquis_val = ${item.v_prod || 0}.  " Valor de Aquisição`);
      lines.push(`  ls_asset_time-curr_type = 'BRL'.  " Moeda`);
      lines.push('');
      
      // Chamada da BAPI
      lines.push('  CALL FUNCTION \'BAPI_ACC_ASSET_CREATE\'');
      lines.push('    EXPORTING');
      lines.push('      companycode    = ls_asset_master-comp_code');
      lines.push('      assetclass     = ls_asset_master-asset_class');
      lines.push('      description    = ls_asset_master-descript');
      lines.push('    IMPORTING');
      lines.push('      asset_number   = lv_asset_number');
      lines.push('    TABLES');
      lines.push('      return         = lt_return.');
      lines.push('');
      
      // Verificação de erro
      lines.push('  READ TABLE lt_return INTO ls_return WITH KEY type = \'E\'.');
      lines.push('  IF sy-subrc = 0.');
      lines.push(`    WRITE: / 'Erro ao criar ativo ${itemNumber}:', ls_return-message.`);
      lines.push('  ELSE.');
      lines.push('    CALL FUNCTION \'BAPI_TRANSACTION_COMMIT\'');
      lines.push('      EXPORTING');
      lines.push('        wait = \'X\'.');
      lines.push(`    WRITE: / 'Ativo ${itemNumber} criado com sucesso:', lv_asset_number.`);
      lines.push('  ENDIF.');
      lines.push('');
      lines.push('  REFRESH: lt_return.');
      lines.push('');
    });
    
    lines.push('END-OF-SELECTION.');
    lines.push('');
    lines.push('*-------------------------------------------------------------------*');
    lines.push('* Fim do programa');
    lines.push('*-------------------------------------------------------------------*');
    
    return lines.join('\n');
  };

  const generateSAPCSVFile = (items: NfeItem[], document: NfeDocument) => {
    const lines: string[] = [];
    
    // Header CSV para importação direta
    lines.push('BUKRS;ANLN1;ANLN2;TXT50;INVNR;SERNR;MENGE;AKTIV;ZUGDT;ANSWL;WAERS;KOSTL;ANLKL');
    
    items.forEach((item) => {
      const classificacao = item.impostos?.classificacao || {};
      const dataAquisicao = document.dh_emi ? new Date(document.dh_emi).toISOString().split('T')[0].replace(/-/g, '') : '';
      
      const row = [
        '1000', // BUKRS - Código da Empresa
        '', // ANLN1 - Número do Ativo (será gerado pelo SAP)
        '', // ANLN2 - Subnúmero do Ativo
        (item.x_prod || '').substring(0, 50).replace(/;/g, ','), // TXT50 - Descrição
        classificacao.patrimonio_tag || '', // INVNR - Número de Inventário
        classificacao.numero_serie || '', // SERNR - Número de Série
        item.q_com || 1, // MENGE - Quantidade
        'X', // AKTIV - Ativo ativo
        dataAquisicao, // ZUGDT - Data de Aquisição
        item.v_prod || 0, // ANSWL - Valor de Aquisição
        'BRL', // WAERS - Moeda
        editCentroCusto || '', // KOSTL - Centro de Custo
        classificacao.categoria_item || 'EQUIP' // ANLKL - Classe do Ativo
      ];
      
      lines.push(row.join(';'));
    });
    
    return lines.join('\n');
  };

  const generateSAPIDOCFile = (items: NfeItem[], document: NfeDocument) => {
    const lines: string[] = [];
    
    // Header IDOC
    lines.push('EDI_DC40');
    lines.push(`TABNAM;EDI_DC40`);
    lines.push(`MANDT;800`);
    lines.push(`DOCNUM;`);
    lines.push(`DOCREL;740`);
    lines.push(`STATUS;`);
    lines.push(`DIRECT;1`);
    lines.push(`OUTMOD;2`);
    lines.push(`IDOCTYP;ASSET_CREATE01`);
    lines.push(`MESTYP;ASSET_CREATE`);
    lines.push(`SNDPOR;ASSETCONTROL`);
    lines.push(`SNDPRT;LS`);
    lines.push(`SNDPRN;ASSETCONTROL`);
    lines.push(`RCVPOR;SAPSYSTEM`);
    lines.push(`RCVPRT;LS`);
    lines.push(`RCVPRN;SAPSYSTEM`);
    lines.push('');
    
    items.forEach((item, index) => {
      const classificacao = item.impostos?.classificacao || {};
      const dataAquisicao = document.dh_emi ? new Date(document.dh_emi).toISOString().split('T')[0].replace(/-/g, '') : '';
      
      // Segmento E1ANLA (Dados Mestres)
      lines.push(`E1ANLA;${index + 1}`);
      lines.push(`BUKRS;1000`);
      lines.push(`ANLKL;${classificacao.categoria_item || 'EQUIP'}`);
      lines.push(`TXT50;${(item.x_prod || '').substring(0, 50)}`);
      lines.push(`INVNR;${classificacao.patrimonio_tag || ''}`);
      lines.push(`SERNR;${classificacao.numero_serie || ''}`);
      lines.push(`MENGE;${item.q_com || 1}`);
      lines.push('');
      
      // Segmento E1ANLZ (Dados de Avaliação)
      lines.push(`E1ANLZ;${index + 1}`);
      lines.push(`ZUGDT;${dataAquisicao}`);
      lines.push(`ANSWL;${item.v_prod || 0}`);
      lines.push(`WAERS;BRL`);
      lines.push(`KOSTL;${editCentroCusto || ''}`);
      lines.push('');
    });
    
    return lines.join('\n');
  };

  const handleExportToSAP = async () => {
    if (!editingDocument || editingItems.length === 0) {
      alert('Nenhum item disponível para exportação');
      return;
    }

    // Filtrar apenas itens que devem ser exportados
    const itemsToExport = editingItems.filter(item => {
      const classificacao = item.impostos?.classificacao;
      return classificacao && (
        classificacao.ativo_sugerido || 
        classificacao.patrimonio_tag || 
        classificacao.numero_serie ||
        classificacao.categoria_item
      );
    });

    if (itemsToExport.length === 0) {
      alert('Nenhum item marcado para exportação. Marque os itens na aba "Itens" antes de exportar.');
      return;
    }

    // Mostrar opções de formato
    const formato = prompt(
      'Escolha o formato de exportação:\n\n' +
      '1 - BAPI (Programa ABAP)\n' +
      '2 - CSV (Importação direta)\n' +
      '3 - IDOC (Integração)\n\n' +
      'Digite o número da opção:'
    );

    if (!formato || !['1', '2', '3'].includes(formato)) {
      return;
    }

    setIsExportingSAP(true);

    try {
      let fileContent = '';
      let fileName = '';
      let mimeType = '';

      switch (formato) {
        case '1':
          fileContent = generateSAPBAPIFile(itemsToExport, editingDocument);
          fileName = `SAP_BAPI_${editingDocument.n_nf}_${editingDocument.serie}_${Date.now()}.txt`;
          mimeType = 'text/plain';
          break;
        case '2':
          fileContent = generateSAPCSVFile(itemsToExport, editingDocument);
          fileName = `SAP_CSV_${editingDocument.n_nf}_${editingDocument.serie}_${Date.now()}.csv`;
          mimeType = 'text/csv';
          break;
        case '3':
          fileContent = generateSAPIDOCFile(itemsToExport, editingDocument);
          fileName = `SAP_IDOC_${editingDocument.n_nf}_${editingDocument.serie}_${Date.now()}.txt`;
          mimeType = 'text/plain';
          break;
      }

      // Criar e fazer download do arquivo
      const blob = new Blob([fileContent], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('✅ Arquivo SAP gerado com sucesso:', fileName);
      console.log('📦 Total de itens exportados:', itemsToExport.length);

      alert(
        `✅ Arquivo SAP gerado com sucesso!\n\n` +
        `📄 Arquivo: ${fileName}\n` +
        `📦 Itens exportados: ${itemsToExport.length}\n\n` +
        `O arquivo foi baixado e está pronto para importação no SAP.`
      );

    } catch (error) {
      console.error('❌ Erro ao gerar arquivo SAP:', error);
      alert('❌ Erro ao gerar arquivo SAP. Tente novamente.');
    } finally {
      setIsExportingSAP(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Notas Fiscais Eletrônicas</h1>
          <button
            onClick={() => setShowXmlImportModal(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors whitespace-nowrap"
          >
            <i className="ri-file-text-line"></i>
            Importar XML
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : documents.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <i className="ri-file-list-3-line text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Nenhuma NF-e encontrada</h3>
            <p className="text-gray-500 mb-6">Comece importando um arquivo XML</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Número
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Emitente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Destinatário
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data Emissão
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor Total
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {documents.map((doc) => (
                  <tr key={doc.ch_nfe} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {doc.n_nf}-{doc.serie}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {doc.emit_xnome || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {doc.dest_xnome || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {doc.dh_emi ? new Date(doc.dh_emi).toLocaleDateString('pt-BR') : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      R$ {doc.v_nf?.toFixed(2) || '0.00'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-right">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => handleOpenEditDrawer(doc.ch_nfe, true)}
                          className="text-blue-600 hover:text-blue-900 w-8 h-8 flex items-center justify-center"
                          title="Consultar"
                        >
                          <i className="ri-eye-line text-lg"></i>
                        </button>
                        <button
                          onClick={() => handleOpenEditDrawer(doc.ch_nfe, false)}
                          className="text-emerald-600 hover:text-emerald-900 w-8 h-8 flex items-center justify-center"
                          title="Editar"
                        >
                          <i className="ri-edit-line text-lg"></i>
                        </button>
                        <button
                          onClick={() => handleOpenDeleteModal(doc.ch_nfe)}
                          className="text-red-600 hover:text-red-900 w-8 h-8 flex items-center justify-center"
                          title="Excluir"
                        >
                          <i className="ri-delete-bin-line text-lg"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Importação XML */}
      {showXmlImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Importar XML</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Selecione o arquivo XML da NF-e
              </label>
              <input
                type="file"
                accept=".xml"
                onChange={handleXmlFileSelect}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            {selectedXmlFile && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  <i className="ri-file-text-line text-emerald-500 mr-2"></i>
                  Arquivo selecionado: {selectedXmlFile.name}
                </p>
              </div>
            )}
            {isUploadingXml && (
              <div className="mb-4">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600 mt-2 text-center">{uploadProgress}%</p>
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowXmlImportModal(false);
                  setSelectedXmlFile(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 whitespace-nowrap"
                disabled={isUploadingXml}
              >
                Cancelar
              </button>
              <button
                onClick={handleQuickXmlUpload}
                disabled={!selectedXmlFile || isUploadingXml}
                className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isUploadingXml ? 'Importando...' : 'Importar NF-e'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-red-100 flex-shrink-0">
                <i className="ri-alert-line text-2xl text-red-600"></i>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">Confirmar Exclusão</h2>
                <p className="text-sm text-gray-600">
                  Essa ação não pode ser desfeita. Escolha o que deseja excluir:
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="deleteType"
                  value="all"
                  checked={deleteType === 'all'}
                  onChange={(e) => setDeleteType(e.target.value as 'all' | 'xml')}
                  className="mt-1 w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500"
                />
                <div>
                  <div className="font-medium text-gray-900">Excluir tudo</div>
                  <div className="text-xs text-gray-600">
                    Remove o lançamento, todos os itens e o arquivo XML
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="deleteType"
                  value="xml"
                  checked={deleteType === 'xml'}
                  onChange={(e) => setDeleteType(e.target.value as 'all' | 'xml')}
                  className="mt-1 w-4 h-4 text-red-600 border-gray-300 focus:ring-red-500"
                />
                <div>
                  <div className="font-medium text-gray-900">Excluir apenas o arquivo XML</div>
                  <div className="text-xs text-gray-600">
                    Mantém o lançamento e os dados, remove apenas o arquivo
                  </div>
                </div>
              </label>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDocumentToDelete(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 whitespace-nowrap"
                disabled={isDeleting}
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isDeleting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Excluindo...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line mr-2"></i>
                    Confirmar Exclusão
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drawer de Edição */}
      {showEditDrawer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end">
          <div className="bg-white w-full max-w-4xl h-full overflow-y-auto shadow-2xl">
            {loadingDetails ? (
              <div className="flex justify-center items-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
              </div>
            ) : editingDocument ? (
              <>
                {/* Header */}
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">
                      {isReadOnly ? 'Consultar NF-e' : 'Editar NF-e'}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Nº {editingDocument.n_nf}-{editingDocument.serie} • {editingDocument.emit_xnome}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isReadOnly && (
                      <button
                        onClick={handleExportToSAP}
                        disabled={isExportingSAP}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-2"
                        title="Exportar para SAP"
                      >
                        {isExportingSAP ? (
                          <>
                            <i className="ri-loader-4-line animate-spin"></i>
                            Exportando...
                          </>
                        ) : (
                          <>
                            <i className="ri-file-download-line"></i>
                            Exportar SAP
                          </>
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => setShowEditDrawer(false)}
                      className="text-gray-500 hover:text-gray-700 w-8 h-8 flex items-center justify-center"
                    >
                      <i className="ri-close-line text-2xl"></i>
                    </button>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b px-6 bg-gray-50">
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`px-4 py-3 font-medium transition-colors whitespace-nowrap ${
                      activeTab === 'details'
                        ? 'text-emerald-600 border-b-2 border-emerald-600'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    <i className="ri-file-info-line mr-2"></i>
                    Detalhes
                  </button>
                  <button
                    onClick={() => setActiveTab('items')}
                    className={`px-4 py-3 font-medium transition-colors whitespace-nowrap ${
                      activeTab === 'items'
                        ? 'text-emerald-600 border-b-2 border-emerald-600'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    <i className="ri-shopping-cart-line mr-2"></i>
                    Itens ({editingItems.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('notes')}
                    className={`px-4 py-3 font-medium transition-colors whitespace-nowrap ${
                      activeTab === 'notes'
                        ? 'text-emerald-600 border-b-2 border-emerald-600'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    <i className="ri-sticky-note-line mr-2"></i>
                    Anotações p/ Ativo
                  </button>
                  <button
                    onClick={() => setActiveTab('xml')}
                    className={`px-4 py-3 font-medium transition-colors whitespace-nowrap ${
                      activeTab === 'xml'
                        ? 'text-emerald-600 border-b-2 border-emerald-600'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    <i className="ri-code-line mr-2"></i>
                    XML
                  </button>
                </div>

                {/* Content */}
                <div className="p-6">
                  {activeTab === 'details' && (
                    <div className="space-y-6">
                      {isReadOnly && (
                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                          <p className="text-sm text-blue-800">
                            <i className="ri-information-line mr-2"></i>
                            Modo de consulta. Para editar, clique no ícone de lápis na tabela.
                          </p>
                        </div>
                      )}

                      {/* Identificação */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="font-bold text-gray-800 mb-3">Identificação</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Chave de Acesso</label>
                            <p className="text-sm font-mono text-gray-900 break-all">{editingDocument.ch_nfe}</p>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Número - Série</label>
                            <p className="text-sm text-gray-900">{editingDocument.n_nf}-{editingDocument.serie}</p>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Data de Emissão</label>
                            <p className="text-sm text-gray-900">
                              {editingDocument.dh_emi ? new Date(editingDocument.dh_emi).toLocaleString('pt-BR') : 'N/A'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Natureza da Operação</label>
                            {isReadOnly ? (
                              <p className="text-sm text-gray-900">{editNatOp || 'N/A'}</p>
                            ) : (
                              <input
                                type="text"
                                value={editNatOp}
                                onChange={(e) => setEditNatOp(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Emitente e Destinatário */}
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="border rounded-lg p-4">
                          <h3 className="font-bold text-gray-800 mb-3">Emitente</h3>
                          <div className="space-y-2">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Razão Social</label>
                              <p className="text-sm font-medium text-gray-900">{editingDocument.emit_xnome}</p>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">CNPJ</label>
                              <p className="text-sm text-gray-900 font-mono">{editingDocument.emit_cnpj}</p>
                            </div>
                          </div>
                        </div>

                        <div className="border rounded-lg p-4">
                          <h3 className="font-bold text-gray-800 mb-3">Destinatário</h3>
                          <div className="space-y-2">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Razão Social</label>
                              <p className="text-sm font-medium text-gray-900">{editingDocument.dest_xnome}</p>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">CNPJ</label>
                              <p className="text-sm text-gray-900 font-mono">{editingDocument.dest_cnpj}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Valores */}
                      <div className="bg-emerald-50 rounded-lg p-4">
                        <h3 className="font-bold text-gray-800 mb-3">Valores</h3>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Produtos</label>
                            <p className="text-sm font-medium text-gray-900">R$ {editingDocument.v_prod?.toFixed(2) || '0.00'}</p>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">ICMS</label>
                            <p className="text-sm font-medium text-gray-900">R$ {editingDocument.v_icms?.toFixed(2) || '0.00'}</p>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">IPI</label>
                            <p className="text-sm font-medium text-gray-900">R$ {editingDocument.v_ipi?.toFixed(2) || '0.00'}</p>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">PIS</label>
                            <p className="text-sm font-medium text-gray-900">R$ {editingDocument.v_pis?.toFixed(2) || '0.00'}</p>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">COFINS</label>
                            <p className="text-sm font-medium text-gray-900">R$ {editingDocument.v_cofins?.toFixed(2) || '0.00'}</p>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Total da NF-e</label>
                            <p className="text-lg font-bold text-emerald-600">R$ {editingDocument.v_nf?.toFixed(2) || '0.00'}</p>
                          </div>
                        </div>
                      </div>

                      {/* Protocolo */}
                      {editingDocument.n_prot && (
                        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                          <h3 className="font-bold text-gray-800 mb-3">Protocolo de Autorização</h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Número do Protocolo</label>
                              <p className="text-sm font-mono text-gray-900">{editingDocument.n_prot}</p>
                            </div>
                            {editingDocument.dh_recbto && (
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">Data de Recebimento</label>
                                <p className="text-sm text-gray-900">
                                  {new Date(editingDocument.dh_recbto).toLocaleString('pt-BR')}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'items' && (
                    <div className="space-y-4">
                      {!isReadOnly && (
                        <div className="bg-blue-50 rounded-lg p-4 mb-4">
                          <p className="text-sm text-blue-800">
                            <i className="ri-information-line mr-2"></i>
                            Você pode editar a descrição dos produtos, adicionar informações para cadastro de ativos e desmembrar itens em múltiplos ativos físicos.
                          </p>
                        </div>
                      )}

                      {editingItems.map((item) => {
                        const dismemberment = itemDismemberments.get(item.id);
                        const isExpanded = expandedItems.has(item.id);

                        return (
                          <div key={item.id} className="border rounded-lg p-4 hover:bg-gray-50">
                            <div className="flex justify-between items-start mb-3">
                              <div>
                                <span className="inline-block bg-emerald-100 text-emerald-800 text-xs font-semibold px-2 py-1 rounded">
                                  Item {item.n_item}
                                </span>
                                <p className="text-xs text-gray-600 mt-1">Código: {item.c_prod}</p>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-gray-900">R$ {item.v_prod?.toFixed(2) || '0.00'}</p>
                                <p className="text-xs text-gray-600">{item.q_com} {item.u_com} × R$ {item.v_un_com?.toFixed(2)}</p>
                              </div>
                            </div>

                            <div className="space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Descrição do Produto</label>
                                {isReadOnly ? (
                                  <p className="text-sm text-gray-900">{item.x_prod}</p>
                                ) : (
                                  <input
                                    type="text"
                                    value={item.x_prod || ''}
                                    onChange={(e) => handleItemFieldChange(item.id, 'x_prod', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                  />
                                )}
                              </div>

                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">NCM</label>
                                  <p className="text-sm text-gray-900 font-mono">{item.ncm}</p>
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-600 mb-1">CFOP</label>
                                  <p className="text-sm text-gray-900 font-mono">{item.cfop}</p>
                                </div>
                              </div>

                              {!isReadOnly && (
                                <>
                                  {/* Seção de Desmembramento Patrimonial */}
                                  <div className="border-t pt-3 mt-3">
                                    <div className="flex items-center justify-between mb-3">
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          id={`dismember-${item.id}`}
                                          checked={dismemberment?.enabled || false}
                                          onChange={() => toggleDismemberment(item.id, item)}
                                          className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                                        />
                                        <label htmlFor={`dismember-${item.id}`} className="text-sm font-semibold text-gray-700">
                                          Gerar ativos físicos (desmembrar)
                                        </label>
                                      </div>
                                      {dismemberment?.enabled && dismemberment.assets_preview.length > 0 && (
                                        <button
                                          onClick={() => toggleItemExpansion(item.id)}
                                          className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                                        >
                                          <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line`}></i>
                                          {isExpanded ? 'Ocultar' : 'Mostrar'} pré-visualização
                                        </button>
                                      )}
                                    </div>

                                    {dismemberment?.enabled && (
                                      <div className="bg-amber-50 rounded-lg p-4 space-y-3">
                                        <div className="grid grid-cols-2 gap-3">
                                          <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                              Quantidade de ativos a gerar
                                            </label>
                                            <input
                                              type="number"
                                              min="1"
                                              value={dismemberment.qtd_ativos}
                                              onChange={(e) => updateDismemberment(item.id, 'qtd_ativos', parseInt(e.target.value) || 1)}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                            />
                                          </div>

                                          <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                              Base de divisão do valor
                                            </label>
                                            <select
                                              value={dismemberment.divisao_tipo}
                                              onChange={(e) => updateDismemberment(item.id, 'divisao_tipo', e.target.value)}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                            >
                                              <option value="nota">Seguir nota (dividir valor total)</option>
                                              <option value="custom">Valor unitário customizado</option>
                                            </select>
                                          </div>
                                        </div>

                                        {dismemberment.divisao_tipo === 'custom' && (
                                          <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                              Valor unitário customizado
                                            </label>
                                            <input
                                              type="number"
                                              step="0.01"
                                              min="0"
                                              value={dismemberment.valor_unitario_custom}
                                              onChange={(e) => updateDismemberment(item.id, 'valor_unitario_custom', parseFloat(e.target.value) || 0)}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                            />
                                            <p className="text-xs text-gray-600 mt-1">
                                              Total gerado: R$ {(dismemberment.valor_unitario_custom * dismemberment.qtd_ativos).toFixed(2)}
                                            </p>
                                          </div>
                                        )}

                                        <button
                                          onClick={() => generateAssetsPreview(item.id, item)}
                                          className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium whitespace-nowrap"
                                        >
                                          <i className="ri-eye-line mr-2"></i>
                                          Pré-visualizar ativos
                                        </button>

                                        {/* Grid de pré-visualização */}
                                        {isExpanded && dismemberment.assets_preview.length > 0 && (
                                          <div className="mt-4 border-t pt-4">
                                            <h4 className="text-sm font-semibold text-gray-800 mb-3">
                                              Pré-visualização dos Ativos ({dismemberment.assets_preview.length})
                                            </h4>
                                            <div className="space-y-3 max-h-96 overflow-y-auto">
                                              {dismemberment.assets_preview.map((asset) => (
                                                <div key={asset.seq} className="bg-white rounded-lg p-3 border">
                                                  <div className="flex items-center justify-between mb-2">
                                                    <span className="text-xs font-semibold text-gray-700">
                                                      Unidade {asset.seq} de {dismemberment.assets_preview.length}
                                                    </span>
                                                    <span className="text-sm font-bold text-emerald-600">
                                                      R$ {asset.valor_base.toFixed(2)}
                                                    </span>
                                                  </div>

                                                  <div className="grid grid-cols-2 gap-2">
                                                    <div>
                                                      <label className="block text-xs text-gray-600 mb-1">
                                                        Tag Patrimonial <span className="text-red-500">*</span>
                                                      </label>
                                                      <input
                                                        type="text"
                                                        value={asset.tag_patrimonial}
                                                        onChange={(e) => updateAssetPreview(item.id, asset.seq, 'tag_patrimonial', e.target.value)}
                                                        placeholder="Ex: PAT-001"
                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                      />
                                                    </div>

                                                    <div>
                                                      <label className="block text-xs text-gray-600 mb-1">
                                                        Nº de Série
                                                      </label>
                                                      <input
                                                        type="text"
                                                        value={asset.numero_serie}
                                                        onChange={(e) => updateAssetPreview(item.id, asset.seq, 'numero_serie', e.target.value)}
                                                        placeholder="Ex: SN123456"
                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                      />
                                                    </div>

                                                    <div className="col-span-2">
                                                      <label className="block text-xs text-gray-600 mb-1">
                                                        Descrição Normalizada
                                                      </label>
                                                      <input
                                                        type="text"
                                                        value={asset.descricao_normalizada}
                                                        onChange={(e) => updateAssetPreview(item.id, asset.seq, 'descricao_normalizada', e.target.value)}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                      />
                                                    </div>

                                                    <div>
                                                      <label className="block text-xs text-gray-600 mb-1">
                                                        Categoria
                                                      </label>
                                                      <input
                                                        type="text"
                                                        value={asset.categoria_item}
                                                        onChange={(e) => updateAssetPreview(item.id, asset.seq, 'categoria_item', e.target.value)}
                                                        placeholder="Ex: Equipamento"
                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                      />
                                                    </div>

                                                    <div className="flex items-center">
                                                      <input
                                                        type="checkbox"
                                                        id={`inv-${item.id}-${asset.seq}`}
                                                        checked={asset.inventariavel}
                                                        onChange={(e) => updateAssetPreview(item.id, asset.seq, 'inventariavel', e.target.checked)}
                                                        className="w-3 h-3 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                                                      />
                                                      <label htmlFor={`inv-${item.id}-${asset.seq}`} className="ml-2 text-xs text-gray-700">
                                                        Inventariável
                                                      </label>
                                                    </div>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>

                                            <div className="mt-3 bg-blue-50 rounded p-3">
                                              <p className="text-xs text-blue-800">
                                                <i className="ri-information-line mr-1"></i>
                                                <strong>Importante:</strong> Todos os itens principais devem ter tag patrimonial única. 
                                                Sub-itens financeiros (BASE, FRETE, IMPOSTOS, SEGURO, OUTROS) serão gerados automaticamente com subnumeração.
                                              </p>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  {/* Classificação para Ativo (mantido para compatibilidade) */}
                                  {!dismemberment?.enabled && (
                                    <div className="bg-gray-50 rounded p-3 space-y-2">
                                      <p className="text-xs font-semibold text-gray-700 mb-2">Classificação para Ativo</p>
                                      
                                      <div>
                                        <label className="block text-xs text-gray-600 mb-1">Categoria do Item</label>
                                        <input
                                          type="text"
                                          value={item.impostos?.classificacao?.categoria_item || ''}
                                          onChange={(e) => handleItemFieldChange(item.id, 'categoria_item', e.target.value)}
                                          placeholder="Ex: Equipamento, Mobiliário, TI..."
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                        />
                                      </div>

                                      <div className="grid grid-cols-2 gap-3">
                                        <div>
                                          <label className="block text-xs text-gray-600 mb-1">Número de Série</label>
                                          <input
                                            type="text"
                                            value={item.impostos?.classificacao?.numero_serie || ''}
                                            onChange={(e) => handleItemFieldChange(item.id, 'numero_serie', e.target.value)}
                                            placeholder="SN123456"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-xs text-gray-600 mb-1">Tag Patrimônio</label>
                                          <input
                                            type="text"
                                            value={item.impostos?.classificacao?.patrimonio_tag || ''}
                                            onChange={(e) => handleItemFieldChange(item.id, 'patrimonio_tag', e.target.value)}
                                            placeholder="PAT-001"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                          />
                                        </div>
                                      </div>

                                      <div className="flex items-center">
                                        <input
                                          type="checkbox"
                                          id={`ativo-${item.id}`}
                                          checked={item.impostos?.classificacao?.ativo_sugerido || false}
                                          onChange={(e) => handleItemFieldChange(item.id, 'ativo_sugerido', e.target.checked)}
                                          className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                                        />
                                        <label htmlFor={`ativo-${item.id}`} className="ml-2 text-sm text-gray-700">
                                          Sugerir como ativo para cadastro
                                        </label>
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {/* Botão de Gerar Ativos */}
                      {!isReadOnly && Array.from(itemDismemberments.values()).some(d => d.enabled && d.assets_preview.length > 0) && (
                        <div className="mt-6 bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-semibold text-gray-800 mb-1">Pronto para gerar ativos?</h4>
                              <p className="text-sm text-gray-600">
                                {Array.from(itemDismemberments.values()).reduce((sum, d) => sum + (d.enabled ? d.assets_preview.length : 0), 0)} ativo(s) 
                                serão criados com sub-itens financeiros e rateio proporcional.
                              </p>
                            </div>
                            <button
                              onClick={handleGenerateAssets}
                              disabled={isGeneratingAssets}
                              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
                            >
                              {isGeneratingAssets ? (
                                <>
                                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                                  Gerando...
                                </>
                              ) : (
                                <>
                                  <i className="ri-add-circle-line mr-2"></i>
                                  Gerar Ativos
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'notes' && (
                    <div className="space-y-4">
                      {!isReadOnly && (
                        <div className="bg-amber-50 rounded-lg p-4 mb-4">
                          <p className="text-sm text-amber-800">
                            <i className="ri-lightbulb-line mr-2"></i>
                            Use esta seção para adicionar informações que ajudarão no cadastro de ativos a partir desta NF-e.
                          </p>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Observações Internas
                        </label>
                        <textarea
                          value={editObservacao}
                          onChange={(e) => setEditObservacao(e.target.value)}
                          rows={4}
                          placeholder="Adicione observações sobre esta nota fiscal..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tags (separadas por vírgula)
                        </label>
                        <input
                          type="text"
                          value={editTags}
                          onChange={(e) => setEditTags(e.target.value)}
                          placeholder="Ex: equipamento, urgente, TI"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Categoria de Ativo
                        </label>
                        <input
                          type="text"
                          value={editCategoria}
                          onChange={(e) => setEditCategoria(e.target.value)}
                          placeholder="Ex: Equipamentos de TI, Mobiliário, Veículos"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Centro de Custo
                        </label>
                        <input
                          type="text"
                          value={editCentroCusto}
                          onChange={(e) => setEditCentroCusto(e.target.value)}
                          placeholder="Ex: Administrativo, Produção, Vendas"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Status de Processamento
                        </label>
                        <select
                          value={editStatus}
                          onChange={(e) => setEditStatus(e.target.value as any)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        >
                          <option value="imported">Importado</option>
                          <option value="reviewed">Revisado</option>
                          <option value="linked_asset">Vinculado a Ativo</option>
                          <option value="ignored">Ignorado</option>
                        </select>
                      </div>

                      <div className="bg-blue-50 rounded-lg p-4 mt-6">
                        <h4 className="font-semibold text-gray-800 mb-2">Próximos Passos</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          <li>• Revise os itens na aba "Itens" e marque quais devem virar ativos</li>
                          <li>• Adicione números de série e tags de patrimônio quando disponível</li>
                          <li>• Após salvar, use o botão "Gerar/Atualizar Ativo" (em breve)</li>
                        </ul>
                      </div>
                    </div>
                  )}

                  {activeTab === 'xml' && (
                    <div>
                      <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <p className="text-sm text-gray-700">
                          <i className="ri-file-code-line mr-2"></i>
                          XML original da NF-e importada
                        </p>
                      </div>
                      <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                        <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap break-all">
                          {formatXml(editingDocument.raw_xml)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer com botões */}
                <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-between items-center">
                  <button
                    onClick={() => setShowEditDrawer(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 whitespace-nowrap"
                    disabled={isSaving}
                  >
                    {isReadOnly ? 'Fechar' : 'Cancelar'}
                  </button>
                  {!isReadOnly && (
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          if (editingDocument) {
                            window.REACT_APP_NAVIGATE(`/assets/register/${editingDocument.ch_nfe}`);
                            setShowEditDrawer(false);
                          }
                        }}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 whitespace-nowrap"
                        title="Abrir cadastro oficial dos ativos"
                      >
                        <i className="ri-file-list-3-line mr-2"></i>
                        Abrir Cadastro Oficial
                      </button>
                      <button
                        onClick={handleSaveChanges}
                        disabled={isSaving}
                        className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {isSaving ? (
                          <>
                            <i className="ri-loader-4-line animate-spin mr-2"></i>
                            Salvando...
                          </>
                        ) : (
                          <>
                            <i className="ri-save-line mr-2"></i>
                            Salvar Alterações
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
